import moment from 'moment';
import { Typography } from 'antd';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2ArrivalNotificationMessage } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getIcs2NotificationMessageLabel } from 'store/declarations/eu/ics2/ics2-notification';
import useICS2Codelists from 'hooks/useICS2Codelists';
import { asList, readDateTime, readField, readText } from '../arrivalMessageUtils';
import DetailsHeader from 'views/declarations/common/ics2-message-details/DetailsHeader';
import MessageDetails from 'views/declarations/common/ics2-message-details/MessageDetails';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

type Props = {
    message: ICS2ArrivalNotificationMessage;
    onBack: () => void;
};

type Subject = { label: string; value: string };

/**
 * What customs named as being under control, as a flat list.
 *
 * The message nests the subject several levels deep - master consignment, house consignments, goods
 * items, packaging, equipment. What the operator needs in order to find the goods is the
 * identifiers, not the shape of the tree, so these are collected and listed. The full structure
 * stays available in the raw message.
 */
const subjectsOf = (consignment: Record<string, unknown>): Subject[] => {
    const subjects: Subject[] = [];

    const collect = (scope: Record<string, unknown>, houseDocument?: string) => {
        asList(scope.postalReceptacle).forEach((receptacle) => {
            const id = receptacle.receptacleIdentificationNumber;
            if (id) subjects.push({ label: 'Receptacle', value: String(id) });
        });

        asList(scope.transportEquipment).forEach((equipment) => {
            if (equipment.identification)
                subjects.push({ label: 'Container', value: String(equipment.identification) });
        });

        asList(scope.consignmentItem).forEach((item) => {
            if (item.sequence === undefined || item.sequence === null) return;
            subjects.push({
                label: houseDocument ? `Goods item (house ${houseDocument})` : 'Goods item',
                value: String(item.sequence),
            });

            asList(item.transportEquipment).forEach((equipment) => {
                if (equipment.identification)
                    subjects.push({ label: 'Container', value: String(equipment.identification) });
            });
        });
    };

    collect(consignment);

    asList(consignment.houseConsignment).forEach((house) => {
        const document = asList(house.transportContractDocument)[0]?.documentNumber;
        const reference = document ? String(document) : undefined;

        if (reference) subjects.push({ label: 'House transport document', value: reference });
        collect(house, reference);
    });

    return subjects;
};

/**
 * Controls decided by customs on the arriving goods.
 *
 * This is the one arrival message with an obligation behind it: the goods named here have to be
 * made available to customs, whether or not they were going to be unloaded at this place. There is
 * no reply message - the response is physical, and anything further happens outside ICS2.
 */
const IE3N08Details = ({ message, onBack }: Props) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    const controlOffice = readText(message, ['customsOfficeOfControl', 'referenceNumber']);
    const scheduledAt = readDateTime(message, ['scheduledControlDate']);
    const notifiedAt = readDateTime(message, ['notificationDate']);
    const messageTypeLabel = getIcs2NotificationMessageLabel(message.messageType);

    const controls = asList(readField(message, ['control']));

    const metadata: Record<string, string> = {
        ...(message.messageType
            ? { 'Message Type': `${message.messageType}${messageTypeLabel ? ` - ${messageTypeLabel}` : ''}` }
            : {}),
        ...(readText(message, ['MRN']) ? { MRN: readText(message, ['MRN'])! } : {}),
        ...(scheduledAt ? { 'Scheduled Control': moment(scheduledAt).format(DATE_FORMAT) } : {}),
        ...(controlOffice
            ? {
                  'Customs Office of Control':
                      getICS2CodeDescription(controlOffice, ICS2CodelistsMap.CL141) || controlOffice,
              }
            : {}),
        ...(notifiedAt ? { 'Issued At': moment(notifiedAt).format(DATE_FORMAT) } : {}),
        Received: moment(message.receivedAt).format(DATE_FORMAT),
    };

    return (
        <>
            <DetailsHeader onBack={onBack} />

            <MessageDetails
                messageMetadata={metadata}
                details={{
                    whatHappened: (
                        <>
                            <Typography.Paragraph>
                                Customs has decided to control part of this arrival. The goods named below must be
                                unloaded and presented for control, even if they were not going to be unloaded here.
                            </Typography.Paragraph>

                            {controls.map((control, index) => {
                                const place = asList(control.examinationPlace)[0];
                                // Either the free-text place or its reference number is given, not both.
                                const namedPlace = place?.name ?? place?.identification;
                                const placeName =
                                    typeof namedPlace === 'string' || typeof namedPlace === 'number'
                                        ? String(namedPlace)
                                        : undefined;
                                const consignment = asList(asList(control.controlSubject)[0]?.consignment)[0];
                                const subjects = consignment ? subjectsOf(consignment) : [];

                                return (
                                    <div key={`control-${index}`} style={{ marginBottom: 16 }}>
                                        {controls.length > 1 && (
                                            <Typography.Text strong>{`Control ${index + 1}`}</Typography.Text>
                                        )}

                                        {placeName && (
                                            <Typography.Paragraph style={{ marginBottom: 4 }}>
                                                {`Place of examination: ${placeName}`}
                                            </Typography.Paragraph>
                                        )}

                                        {subjects.length ? (
                                            <ul style={{ listStyleType: 'initial', marginBottom: 0 }}>
                                                {subjects.map((subject, subjectIndex) => (
                                                    <li key={`${subject.label}-${subject.value}-${subjectIndex}`}>
                                                        {subject.label}: <strong>{subject.value}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <Typography.Text type="secondary">
                                                Customs did not narrow the control to particular goods. See the raw
                                                message for the full structure.
                                            </Typography.Text>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    ),
                    nextSteps: (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            <li>Arrange for the named goods to be unloaded and presented to customs.</li>
                            <li>Nothing is sent back through ICS2 - this notification expects no reply message.</li>
                            <li>
                                Agree with the party handling the unloading who presents the goods, if that is not you.
                            </li>
                            <li>
                                While a control is outstanding, the related ENS can no longer be amended or invalidated.
                            </li>
                        </ul>
                    ),
                }}
            />
        </>
    );
};

export default IE3N08Details;
